/**
 * Tabulation inputs that are not scores: adjustments, disqualification, advancement.
 *
 * Every schema here demands a reason. An adjustment moves a rank and therefore money, and a
 * disqualification removes a dancer from a standing that has already been announced — both are the
 * "friction where money and fairness live" case (ux.md §2), and `score_adjustments.reason` is NOT
 * NULL in the schema for the same reason this is not optional here.
 */

import { z } from "zod";
import { reasonSchema, shortTextSchema, ulidSchema } from "./primitives.js";

export const ADJUSTMENT_KINDS = ["penalty", "deduction", "bonus", "correction"] as const;

/**
 * A tabulator's adjustment to a computed total (D34).
 *
 * `points` is the **signed** delta in display points, which is why it is not `centsSchema`-like
 * and not bounded below at zero: a five-point timing penalty is `-5`, and asking the interface to
 * infer the sign from `kind` would make "bonus of -3" expressible and meaningless.
 */
export const createAdjustmentSchema = z.object({
  registrationEntryId: ulidSchema,
  phaseId: ulidSchema.optional(),
  kind: z.enum(ADJUSTMENT_KINDS),
  /** What it is called on the trace and the printed sheet — "Late to the arena". */
  label: shortTextSchema,
  points: z.number().int().min(-100_000).max(100_000),
  reason: reasonSchema,
});

export const removeAdjustmentSchema = z.object({
  reason: reasonSchema,
});

/**
 * Disqualify or reinstate an entry.
 *
 * The reason is mandatory in both directions: putting someone back into a standing is as
 * consequential as taking them out of it, and an unexplained reinstatement is the one an audit
 * would ask about.
 */
export const disqualifyEntrySchema = z.object({
  reason: reasonSchema,
});

export const reinstateEntrySchema = z.object({
  reason: reasonSchema,
});

export type CreateAdjustmentInput = z.infer<typeof createAdjustmentSchema>;
